/**
 * "New version available" notice.
 *
 * Shows when src/lib/buildWatch.js finds that the server now has a different
 * build from the one this window is running. It offers a Reload button and never
 * reloads on its own: people are mid-quote or mid-form, and a reload clears
 * whatever is typed and not saved. ✕ hides it until the server has yet another
 * build.
 *
 * Where it is mounted (a screen without it never asks the server). The screen
 * places it, through className; this file only draws the card.
 *   - Layout.jsx, twice: at the foot of the desktop sidebar (stacked) and above
 *     the phone's bottom bar. Layout's CSS shows one of the two.
 *   - LoginScreen.jsx, inside the sign-in card. A signed-out window can sit
 *     there for days.
 * Deliberately not on the invite and set-password screens or the Jobber callback
 * (App.jsx): a reload there drops the step in progress. Not on the client hub
 * (/hub) either: its sign-in link lives only in memory until it is redeemed, and
 * the one bundle changes for crew-side work a client has no use hearing about.
 *
 * Inline styles, with Layout's colour variables and their values as fallbacks so
 * it looks the same where Layout's CSS is not on the page.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import { buildWatch } from "../lib/buildWatch";

// A press this soon after the notice appears is ignored. It appears just after
// the window is looked at again, which is when a finger or the pointer is
// already on its way to something else, and Reload clears unsaved work.
export const ARM_MS = 700;

/** The build on offer (a file name) while the notice should show, else null. */
export function useNewBuild(watch = buildWatch) {
  return useSyncExternalStore(watch.subscribe, watch.getSnapshot);
}

export default function NewVersionNotice({ className, stacked = false, note = "Finish what you're doing, then reload.", watch = buildWatch }) {
  const offered = useNewBuild(watch);
  const shownAt = useRef(null);
  useEffect(() => { shownAt.current = offered ? watch.now() : null; }, [offered, watch]);

  if (!offered) return null;

  const armed = () => {
    if (shownAt.current == null) return false;
    const since = watch.now() - shownAt.current;
    return since >= ARM_MS || since < 0; // since < 0: the clock was set back; do not leave the buttons dead
  };
  const onReload = () => { if (armed()) watch.reload(); };
  const onDismiss = () => { if (armed()) watch.dismiss(); };

  const close = (
    <button
      type="button"
      onClick={onDismiss}
      style={S.close}
      aria-label="Hide until the next version"
      title="Hide until the next version"
    >
      ✕
    </button>
  );
  const reload = (
    <button type="button" onClick={onReload} style={stacked ? S.reloadWide : S.reload}>
      Reload
    </button>
  );

  // One card, two shapes. Wide: the words sit beside the button. Stacked, for the
  // 200px sidebar: the title gets the whole first line and ✕ moves next to Reload.
  return (
    <div className={className} role="status" aria-live="polite">
      <div style={S.card} title={`This window is running ${watch.state().running}. The server now has ${offered}.`}>
        <div style={S.head}>
          <span style={S.dot} aria-hidden="true" />
          <span style={S.title}>New version available</span>
          {!stacked && close}
        </div>
        <div style={stacked ? undefined : S.row}>
          <div style={S.sub}>{note}</div>
          {stacked ? <div style={S.actions}>{reload}{close}</div> : reload}
        </div>
      </div>
    </div>
  );
}

const C = {
  card: "var(--surface2, #1e2328)",
  accent: "var(--accent, #a5ce3a)",
  text: "var(--text, #e8eaf0)",
  muted: "var(--text-muted, #8892a4)",
  ink: "var(--bg, #0d0f12)",
};

const RELOAD = {
  flexShrink: 0, marginRight: 4, padding: "9px 14px",
  background: C.accent, color: C.ink, border: "none", borderRadius: 8,
  fontSize: 12.5, fontWeight: 700, fontFamily: "inherit", lineHeight: 1.2,
  whiteSpace: "nowrap", cursor: "pointer",
};

const S = {
  card: {
    boxSizing: "border-box", width: "100%",
    padding: "8px 8px 10px 12px",
    background: C.card, color: C.text,
    border: `1px solid ${C.accent}`, borderRadius: 12,
    boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
    fontFamily: "'Roboto',system-ui,sans-serif", lineHeight: 1.3, textAlign: "left",
  },
  head: { display: "flex", alignItems: "center", gap: 8 },
  dot: { width: 7, height: 7, borderRadius: "50%", background: C.accent, flexShrink: 0 },
  title: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: C.text },
  close: {
    flexShrink: 0, width: 32, height: 30, margin: 0, padding: 0,
    background: "none", border: "none", borderRadius: 6,
    color: C.muted, fontSize: 13, lineHeight: 1, fontFamily: "inherit", cursor: "pointer",
  },
  row: { display: "flex", alignItems: "center", gap: 10 },
  sub: { flex: 1, minWidth: 0, margin: "2px 4px 0 0", fontSize: 11.5, color: C.muted },
  actions: { display: "flex", alignItems: "center", gap: 2, marginTop: 8 },
  reload: RELOAD,
  reloadWide: { ...RELOAD, flex: 1, marginRight: 0 },
};
